import { useCallback, useEffect, useRef, useState } from "react";
import { normalizeForSpeech } from "../utils/normalizeForSpeech";

type PlaybackState = "idle" | "playing";

/** "browser" uses the Web Speech API speechSynthesis (cloud default).
 *  "kokoro"  fetches MP3 audio from the backend /tts endpoint (Kokoro TTS). */
type AudioMode = "browser" | "kokoro";

/**
 * useAudioPlayer — TTS abstraction supporting two modes:
 *
 *   "browser" (default / cloud): uses window.speechSynthesis — no backend.
 *   "kokoro"  (local):           POSTs each sentence to /tts, plays the
 *                                 returned MP3 via the HTML5 Audio API.
 *
 * Public API is identical in both modes so Chat.tsx needs no structural change.
 *
 * unlock() — call inside a user-gesture handler before async speech.
 *            Browser mode: fires a silent utterance to open the iOS audio session.
 *            Kokoro mode:  no-op (Audio.play() works without it locally).
 * play(text)  — speak a full string immediately (cancels current speech)
 * queue(text) — append a sentence; drains one-at-a-time to avoid iOS drops
 * stop()      — cancel all speech and clear the pending queue
 *
 * --- Kokoro gap elimination ---
 * Naive sequential playback introduces a ~300–800 ms gap between sentences
 * because the next /tts fetch only starts after the current audio ends.
 * We solve this with a one-sentence lookahead:
 *   • When sentence N starts playing, immediately kick off a fetch for sentence N+1.
 *   • Store the result in prefetchRef (a Promise<HTMLAudioElement | null>).
 *   • When sentence N ends, await the already-in-flight promise instead of
 *     starting a new fetch — the gap shrinks to near zero.
 *
 * --- Browser synthesis resume poke ---
 * Chrome and some Safari versions silently pause speechSynthesis after a few
 * seconds (background tab, hardware scheduler, known engine bugs).  We install
 * a 10-second poking timer that calls resume() whenever the engine is paused
 * but the queue is non-empty.  The timer is cleared when speech goes idle.
 */
/**
 * Resolve the best available English voice for speechSynthesis.
 *
 * Chrome populates getVoices() asynchronously — the first call often returns [].
 * Preferred priority order:
 *   1. An enhanced/premium en-US voice (e.g. "Samantha" on macOS, "Google US English" on Chrome)
 *   2. Any en-US voice
 *   3. Any en-* voice
 *   4. Whatever the browser defaults to (null → let browser decide)
 *
 * The result is cached in a module-level variable so every hook instance
 * shares it and we only resolve once per page load.
 */
let _cachedVoice: SpeechSynthesisVoice | null | undefined = undefined; // undefined = not yet resolved

function _pickVoice(): SpeechSynthesisVoice | null {
  if (!("speechSynthesis" in window)) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;

  // 1. Premium en-US (macOS Samantha, Google US English, Microsoft Aria Online, etc.)
  const premium = voices.find(
    (v) => v.lang === "en-US" && /samantha|google us english|aria online|zira online|mark online/i.test(v.name),
  );
  if (premium) return premium;

  // 2. Any en-US
  const enUS = voices.find((v) => v.lang === "en-US");
  if (enUS) return enUS;

  // 3. Any English
  const en = voices.find((v) => v.lang.startsWith("en"));
  return en ?? null;
}

function _resolveVoice(): SpeechSynthesisVoice | null {
  if (_cachedVoice !== undefined) return _cachedVoice;
  const v = _pickVoice();
  if (v !== null) {
    _cachedVoice = v;
  }
  return v;
}

export function useAudioPlayer(mode: AudioMode = "browser") {
  const [playbackState, setPlaybackState] = useState<PlaybackState>("idle");

  // Shared sentence queue
  const pendingRef  = useRef<string[]>([]);
  const speakingRef = useRef(false);

  // Kokoro: reference to the currently playing <audio> element so stop() can abort it
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  // Browser: cached preferred voice (updated when voiceschanged fires)
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null);

  // Pre-load voices as early as possible so the first utterance has a voice ready.
  // Chrome fires voiceschanged once the async voice list is populated.
  useEffect(() => {
    if (mode !== "browser" || !("speechSynthesis" in window)) return;

    const load = () => {
      const v = _resolveVoice();
      voiceRef.current = v;
    };

    load(); // may succeed immediately on Firefox / Safari
    window.speechSynthesis.addEventListener("voiceschanged", load);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", load);
  }, [mode]);

  // Kokoro: one-sentence lookahead — promise resolves to the pre-fetched audio element
  // (or null on fetch error).  Consumed once by the next _drainKokoro call.
  const prefetchRef = useRef<Promise<HTMLAudioElement | null> | null>(null);

  // Stable ref to the active drain fn — audio.onended closures read this
  // rather than capturing a stale closure from the render they were created in.
  const drainRef = useRef<() => void>(() => {});

  // Browser: timer handle for the synthesis resume poke
  const resumePokeRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Autoplay-blocked detection.  Tracks the most recent speak() — if its
  // `onstart` event never fires within AUTOPLAY_TIMEOUT_MS, iOS / Safari
  // has silently dropped the utterance (typical on backgrounded tabs or
  // when the user-gesture context has expired before audio could begin).
  // We flip `autoplayBlocked = true` so the UI can prompt the user to tap
  // a "Play" affordance and recover.
  const autoplayCheckRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const AUTOPLAY_TIMEOUT_MS = 1500;

  // -------------------------------------------------------------------------
  // Browser — speechSynthesis helpers
  // -------------------------------------------------------------------------

  const _makeUtterance = useCallback(
    (text: string, onEnd?: () => void): SpeechSynthesisUtterance => {
      const u = new SpeechSynthesisUtterance(normalizeForSpeech(text));
      u.lang    = "en-US";
      u.rate    = 1.05;
      u.pitch   = 1.0;
      // Assigning a resolved voice skips Chrome's per-utterance voice-lookup
      // step, which can add 100–500 ms of startup latency on cold synthesis.
      const v = voiceRef.current ?? _resolveVoice();
      if (v) u.voice = v;
      u.onstart = () => {
        setPlaybackState("playing");
        // Autoplay just succeeded — clear any blocked-state indicator.
        if (autoplayCheckRef.current !== null) {
          clearTimeout(autoplayCheckRef.current);
          autoplayCheckRef.current = null;
        }
        setAutoplayBlocked(false);
      };
      u.onend   = () => onEnd?.();
      u.onerror = () => onEnd?.();
      return u;
    },
    [],
  );

  /** Start a timer that pokes speechSynthesis.resume() every 10 s.
   *  Chrome and some Safari versions silently pause the synthesis engine;
   *  this keeps it alive for the duration of a narration. */
  const _startResumePoker = useCallback(() => {
    if (resumePokeRef.current !== null) return; // already running
    if (!("speechSynthesis" in window)) return;
    resumePokeRef.current = setInterval(() => {
      if (window.speechSynthesis.paused && speakingRef.current) {
        window.speechSynthesis.resume();
      }
    }, 10_000);
  }, []);

  const _stopResumePoker = useCallback(() => {
    if (resumePokeRef.current !== null) {
      clearInterval(resumePokeRef.current);
      resumePokeRef.current = null;
    }
  }, []);

  /** Drain one sentence from the queue via speechSynthesis, chain to the next. */
  const _drainBrowser = useCallback(() => {
    if (!("speechSynthesis" in window)) return;
    const next = pendingRef.current.shift();
    if (!next) {
      speakingRef.current = false;
      setPlaybackState("idle");
      _stopResumePoker();
      return;
    }
    speakingRef.current = true;
    setPlaybackState("playing");
    _startResumePoker();
    // Arm the autoplay-blocked watchdog.  If the utterance fires `onstart`
    // before this timer expires (see _makeUtterance), the timer is cleared
    // and `autoplayBlocked` stays false.  If iOS Safari silently drops the
    // utterance (no `onstart` ever), the watchdog flips `autoplayBlocked`
    // so the UI can show a tap-to-play affordance.
    if (autoplayCheckRef.current !== null) clearTimeout(autoplayCheckRef.current);
    autoplayCheckRef.current = setTimeout(() => {
      setAutoplayBlocked(true);
      autoplayCheckRef.current = null;
    }, AUTOPLAY_TIMEOUT_MS);
    // Use drainRef so the onEnd callback always calls the latest drain function.
    window.speechSynthesis.speak(_makeUtterance(next, () => drainRef.current()));
  }, [_makeUtterance, _startResumePoker, _stopResumePoker]);

  // -------------------------------------------------------------------------
  // Kokoro — fetch /tts → MP3 blob → HTML5 Audio
  // -------------------------------------------------------------------------

  /** Fetch one sentence from /tts and return a ready-to-play Audio element.
   *  Returns null on any network or HTTP error (drain will skip gracefully). */
  const _fetchKokoroAudio = useCallback(
    (text: string): Promise<HTMLAudioElement | null> =>
      fetch("/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      })
        .then((resp) => {
          if (!resp.ok) throw new Error(`/tts returned ${resp.status}`);
          return resp.blob();
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          (audio as any)._objectUrl = url; // stash for cleanup in onDone
          return audio as HTMLAudioElement;
        })
        .catch((err) => {
          console.error("Kokoro TTS fetch failed:", err);
          return null;
        }),
    [],
  );

  /**
   * Drain one sentence from the queue via the Kokoro /tts endpoint.
   *
   * Lookahead pre-fetch:
   *   1. Use prefetchRef.current if a pre-fetch is already in flight (set by
   *      the previous drain call).  This hides fetch latency behind playback.
   *   2. Once this sentence's audio resolves, immediately kick off the next
   *      fetch for pendingRef[0] (if any) and store it in prefetchRef.
   *   3. When audio ends, call drainRef.current() — which will find the
   *      pre-fetched promise waiting in prefetchRef.
   */
  const _drainKokoro = useCallback(() => {
    // Consume any in-flight pre-fetch promise, then reset the slot.
    const inFlight = prefetchRef.current;
    prefetchRef.current = null;

    const next = pendingRef.current.shift();
    if (!next) {
      speakingRef.current = false;
      setPlaybackState("idle");
      return;
    }
    speakingRef.current = true;
    setPlaybackState("playing");

    // Resolve audio: either the pre-fetched promise or a fresh fetch.
    const audioPromise = inFlight ?? _fetchKokoroAudio(next);

    audioPromise.then((audio) => {
      if (!audio) {
        // Fetch failed — skip this sentence and move on
        currentAudioRef.current = null;
        drainRef.current();
        return;
      }

      // ── Lookahead: start fetching the next sentence NOW, while this one plays ──
      const upNext = pendingRef.current[0];
      if (upNext !== undefined && prefetchRef.current === null) {
        prefetchRef.current = _fetchKokoroAudio(upNext);
      }

      currentAudioRef.current = audio;
      const onDone = () => {
        // Clean up the object URL that _fetchKokoroAudio attached
        const url = (audio as any)._objectUrl as string | undefined;
        if (url) URL.revokeObjectURL(url);
        currentAudioRef.current = null;
        drainRef.current(); // play next — will use prefetchRef if ready
      };
      audio.onended = onDone;
      audio.onerror = onDone;
      audio.play().catch(onDone);
    });
  }, [_fetchKokoroAudio]);

  // Always keep drainRef pointing at the active implementation.
  drainRef.current = mode === "kokoro" ? _drainKokoro : _drainBrowser;

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Browser mode: fires a silent utterance inside the user-gesture context so
   * iOS Safari keeps the audio session alive for subsequent async speak() calls.
   *
   * Important: do NOT call cancel() here — the utterance must finish naturally
   * so iOS holds the session open for the first narrator sentence.
   *
   * Kokoro mode: no-op.
   */
  const unlock = useCallback(() => {
    if (mode !== "browser" || !("speechSynthesis" in window)) return;
    // Fire a near-silent utterance inside the user-gesture context so iOS
    // Safari keeps the audio session alive for subsequent async speak() calls.
    //
    // We also assign the preferred voice here so the synthesis engine warms up
    // with the same voice the narrator will use — this eliminates the ~500 ms
    // cold-start delay that Chrome adds when a voice is resolved for the first
    // time on a real utterance.
    //
    // The speak() call is deferred by 50ms so that any immediately-preceding
    // speechSynthesis.cancel() (e.g. from stop() called moments earlier) has
    // time to flush.  Without this delay, WebKit on iOS occasionally drops
    // the unlock utterance along with the cancelled queue — leaving the audio
    // session NOT unlocked for the rest of the request.  The user-gesture
    // activation window on iOS is ~5 seconds, well above 50ms, so deferring
    // does not lose gesture-initiated status.
    setTimeout(() => {
      if (!("speechSynthesis" in window)) return;
      const u = new SpeechSynthesisUtterance(" "); // audible token required for Chrome warm-up
      const v = voiceRef.current ?? _resolveVoice();
      if (v) u.voice = v;
      u.lang   = "en-US";
      u.volume = 0.01; // near-silent but not zero — iOS needs a real audio frame
      u.rate   = 10;
      window.speechSynthesis.speak(u);
    }, 50);
  }, [mode]);

  const stop = useCallback(() => {
    pendingRef.current  = [];
    speakingRef.current = false;
    prefetchRef.current = null; // discard any in-flight pre-fetch
    setPlaybackState("idle");
    if (mode === "browser") {
      _stopResumePoker();
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    } else {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
    }
  }, [mode, _stopResumePoker]);

  /** Speak text immediately, cancelling anything currently in progress. */
  const play = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      stop();
      pendingRef.current  = [text];
      speakingRef.current = true;
      setPlaybackState("playing");
      drainRef.current();
    },
    [stop],
  );

  /**
   * Append a sentence to the internal queue.
   *
   * Kokoro lookahead: if audio is already playing and there is no pre-fetch
   * in flight, kick one off immediately for this new sentence so it arrives
   * before the current audio ends.
   */
  const queue = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      pendingRef.current.push(text);

      if (mode === "kokoro" && speakingRef.current && prefetchRef.current === null) {
        // A sentence just arrived while audio is playing and nothing is pre-fetched yet.
        // Start fetching immediately so it's ready when the current audio ends.
        prefetchRef.current = _fetchKokoroAudio(text);
      }

      if (!speakingRef.current) drainRef.current();
    },
    [mode, _fetchKokoroAudio],
  );

  /** Manually clear the autoplayBlocked flag — call after the user taps a
   *  fallback Play affordance and audio has actually started. */
  const dismissAutoplayBlocked = useCallback(() => {
    if (autoplayCheckRef.current !== null) {
      clearTimeout(autoplayCheckRef.current);
      autoplayCheckRef.current = null;
    }
    setAutoplayBlocked(false);
  }, []);

  return { play, queue, stop, unlock, playbackState, autoplayBlocked, dismissAutoplayBlocked };
}
